import React, { useState, useEffect } from "react";
import { PersonStanding, SunMoon, Link2, RotateCcw } from "lucide-react";

const AccessibilityPanel = ({ language, className = "" }) => {
  // Prevent carry-over clicks from the open button triggering toggles immediately
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const [fontSize, setFontSize] = useState(
    () => localStorage.getItem("a11y_fontSize") || "md"
  );
  const [highContrast, setHighContrast] = useState(
    () => localStorage.getItem("a11y_highContrast") === "true"
  );
  const [linkHighlight, setLinkHighlight] = useState(
    () => localStorage.getItem("a11y_linkHighlight") === "true"
  );

  const t =
    language === "el"
      ? {
          title: "Προσβασιμότητα",
          fontSizeLabel: "Μέγεθος γραμματοσειράς",
          highContrast: "Υψηλή αντίθεση",
          linkHighlight: "Επισήμανση συνδέσμων",
          reset: "Επαναφορά",
        }
      : {
          title: "Accessibility",
          fontSizeLabel: "Font size",
          highContrast: "High contrast",
          linkHighlight: "Highlight links",
          reset: "Reset",
        };

  const handleFontSize = (size) => {
    setFontSize(size);
    localStorage.setItem("a11y_fontSize", size);
    document.documentElement.classList.remove("font-sm", "font-lg");
    if (size === "sm") document.documentElement.classList.add("font-sm");
    if (size === "lg") document.documentElement.classList.add("font-lg");
  };

  // Apply / remove accessibility-mode class only when the toggle changes,
  // NOT on initial mount (startup is handled in index.js to avoid the class
  // being applied the moment the panel opens).
  const mountedRef = React.useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    document.documentElement.classList.toggle("accessibility-mode", highContrast);
  }, [highContrast]);

  const handleHighContrast = () => {
    const next = !highContrast;
    setHighContrast(next);
    localStorage.setItem("a11y_highContrast", String(next));
    document.documentElement.classList.toggle("accessibility-mode", next);
  };

  const mountedLinkRef = React.useRef(false);
  useEffect(() => {
    if (!mountedLinkRef.current) {
      mountedLinkRef.current = true;
      return;
    }
    document.documentElement.classList.toggle("link-highlight", linkHighlight);
  }, [linkHighlight]);

  const handleLinkHighlight = () => {
    const next = !linkHighlight;
    setLinkHighlight(next);
    localStorage.setItem("a11y_linkHighlight", String(next));
    document.documentElement.classList.toggle("link-highlight", next);
  };

  const handleReset = () => {
    setFontSize("md");
    setHighContrast(false);
    setLinkHighlight(false);
    localStorage.removeItem("a11y_fontSize");
    localStorage.removeItem("a11y_highContrast");
    localStorage.removeItem("a11y_linkHighlight");
    document.documentElement.classList.remove("accessibility-mode");
    document.documentElement.classList.remove("link-highlight");
    document.documentElement.classList.remove("font-sm", "font-lg");
  };

  const Toggle = ({ checked, onChange, label }) => (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-custom-grad1 ${
        checked ? "bg-custom-grad1" : "bg-custom-letters/25"
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-4" : "translate-x-0.5"
        }`}
      />
    </button>
  );

  return (
    <div
      className={`a11y-panel absolute z-[200] bg-custom-header border border-custom-letters/20 rounded-2xl shadow-2xl p-4 w-60 ${className}`}
      role="dialog"
      aria-label={t.title}
      onClick={(e) => e.stopPropagation()}
      style={{ pointerEvents: ready ? "auto" : "none" }}
    >
      {/* Title */}
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-custom-letters/20">
        <PersonStanding size={18} className="text-custom-grad1" />
        <span className="font-bold text-custom-letters text-sm">{t.title}</span>
      </div>

      {/* Font size */}
      <div className="mb-4">
        <p className="text-xs text-custom-letters/80 mb-2 leading-snug">
          {t.fontSizeLabel}
        </p>
        <div className="flex gap-1">
          {[
            { value: "sm", label: "A−", textClass: "text-xs" },
            { value: "md", label: "A", textClass: "text-sm" },
            { value: "lg", label: "A+", textClass: "text-base" },
          ].map(({ value, label, textClass }) => (
            <button
              key={value}
              onClick={() => handleFontSize(value)}
              className={`flex-1 rounded-lg border py-1 font-bold transition-colors focus:outline-none focus:ring-2 focus:ring-custom-grad1 ${textClass} ${
                fontSize === value
                  ? "bg-custom-grad1 border-custom-grad1 text-white"
                  : "border-custom-letters/40 text-custom-letters hover:border-custom-grad1"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* High Contrast */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <SunMoon size={14} className="text-custom-letters/70" />
          <span className="text-xs text-custom-letters">{t.highContrast}</span>
        </div>
        <Toggle
          checked={highContrast}
          onChange={handleHighContrast}
          label={t.highContrast}
        />
      </div>

      {/* Link Highlight */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-1.5">
          <Link2 size={14} className="text-custom-letters/70" />
          <span className="text-xs text-custom-letters">{t.linkHighlight}</span>
        </div>
        <Toggle
          checked={linkHighlight}
          onChange={handleLinkHighlight}
          label={t.linkHighlight}
        />
      </div>

      {/* Reset */}
      <div className="border-t border-custom-letters/20 pt-3">
        <button
          id="a11y-reset-btn"
          onClick={handleReset}
          className="w-full flex items-center justify-center gap-1.5 text-xs text-custom-grad4 hover:text-custom-grad1 transition focus:outline-none focus:ring-2 focus:ring-custom-grad1 rounded"
        >
          <RotateCcw size={12} />
          {t.reset}
        </button>
      </div>
    </div>
  );
};

export default AccessibilityPanel;
